import { useState } from 'react';
import dayJs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Typography } from '@mui/material';

import { appConfig } from '@app/app';
import { Label } from '@app/ui/label';
import { Tooltip } from '@app/ui/tooltip';

import { RuleSetVersion, RuleSetStatus } from '../types';
import { DRY_RUN_SUPPORTED_FAMILIES } from '../constants';
import { RestoreVersionDialog } from './RestoreVersionDialog';

export interface VersionHistoryProps {
  readonly versions: RuleSetVersion[];
  readonly activeVersion: RuleSetVersion | null;
  readonly onEvaluate: (version: RuleSetVersion) => void;
  readonly onRestored: () => void;
}

export const VersionHistory = ({ versions, activeVersion, onEvaluate, onRestored }: VersionHistoryProps) => {
  const { t } = useTranslation('common');

  // A restored version's content came from the restored one, not from the live version it was opened on.
  const versionOrigin = (version: RuleSetVersion) => {
    if (version.restoredFromVersion != null) {
      return `${t('rules.versionHistory.columns.restoredFrom')}: v${version.restoredFromVersion}`;
    }

    if (version.parentVersion != null) {
      return `${t('rules.versionHistory.columns.parent')}: v${version.parentVersion}`;
    }

    return t('rules.versionHistory.noParent');
  };

  const [restoreSource, setRestoreSource] = useState<RuleSetVersion | null>(null);

  const handleRestored = () => {
    setRestoreSource(null);
    onRestored();
  };

  return (
    <Paper variant="outlined" elevation={0} sx={{ borderRadius: '16px', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {t('rules.versionHistory.title')}
      </Typography>

      {!versions.length && (
        <Typography variant="body1" color="text.secondary">
          {t('rules.versionHistory.empty')}
        </Typography>
      )}

      {versions.map(version => {
        return (
          <Box
            key={version.id}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'grey.300',
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <Typography variant="subtitle1" sx={{ minWidth: 60, fontWeight: 600 }}>
              v{version.version}
            </Typography>

            <Label
              text={
                version.status === RuleSetStatus.published
                  ? t('rules.versionHistory.statusPublished')
                  : t('rules.versionHistory.statusDraft')
              }
              color={version.status === RuleSetStatus.published ? 'success' : 'info'}
              sx={{ lineHeight: 'normal' }}
            />

            <Typography variant="body1" color="text.secondary" sx={{ minWidth: 160 }}>
              {version.authorName ?? t('rules.versionHistory.unknownAuthor')}
              {' — '}
              {dayJs(version.publishedAt ?? version.createdAt).format(appConfig.format.date)}
            </Typography>

            <Typography variant="body1" sx={{ flexGrow: 1, minWidth: 200 }}>
              {versionOrigin(version)}
            </Typography>

            <Tooltip
              title={
                (!DRY_RUN_SUPPORTED_FAMILIES.includes(version.family)
                  ? t('rules.summary.dryRunDisabledTooltip')
                  : t('rules.versionHistory.evaluateTooltip')) ?? undefined
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primaryLight"
                  disabled={!DRY_RUN_SUPPORTED_FAMILIES.includes(version.family)}
                  onClick={() => onEvaluate(version)}
                >
                  {t('rules.versionHistory.evaluateButton')}
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                (version.status === RuleSetStatus.published
                  ? t('rules.restore.tooltip')
                  : t('rules.restore.draftOnlyTooltip')) ?? undefined
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primaryLight"
                  disabled={version.status !== RuleSetStatus.published}
                  onClick={() => setRestoreSource(version)}
                >
                  {t('rules.restore.button')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        );
      })}

      {restoreSource && (
        <RestoreVersionDialog
          open
          onClose={() => setRestoreSource(null)}
          source={restoreSource}
          activeVersion={activeVersion}
          onRestored={handleRestored}
        />
      )}
    </Paper>
  );
};
